import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { APIProvider, Map, MapControl, ControlPosition, AdvancedMarker } from '@vis.gl/react-google-maps'
import { Box, useTheme, useMediaQuery, LinearProgress, DialogContent, SvgIcon, Dialog } from '@mui/material'
import { useConfiguredServiceMapData } from 'components/configured-service/hooks/useConfiguredServiceMapData'
import { MAX_AUTO_FIT_MARKER_COUNT } from 'settings/constants/map'
import { useFormContext } from 'react-hook-form'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import {
  CREATE_DRAFT_CS_TABS,
  selectCSDCurrentTab,
  selectCSDIsMapReady,
  selectCSDMapCamera,
  selectCSDMapMarkerType,
  setIsMapReady,
  setMapCamera,
  setMapMarkerType,
} from 'slices/configuredService/createDraftCSDSlice'
import { WEEK_DAYS } from 'settings/constants/day'
import { ReactComponent as CSMarker } from 'assets/map/CSMarker.svg'
import { Circle } from 'components/configured-service/Schedule/Circle'
import MarkerTypeControl from 'components/configured-service/tabs/MapRoutingTab/components/MarkerTypeControl'
import WeekPicker from 'components/configured-service/Schedule/WeekPicker'
import MapTypeControlWrapper from 'components/common/map/MapTypeControlWrapper'
import { GOOGLE_MAPS_MAX_ZOOM } from 'components/common/map/constants'
import Constants from 'Constants'
import MapRadiusControl from 'components/configured-service/Schedule/MapRadiusControl'
import { endOfWeek } from 'date-fns'
import { formatDateToBEFormatDateFns } from 'utils/date'
import { blue } from '@mui/material/colors'
import HHDialogTitle from 'components/common/HHDialogTitle'
import { Z_INDEX } from 'theme/zIndex'
import { get } from 'lodash'
import { formatCentsToDollars } from 'utils/price'
import { useMapVisibleMarkers } from 'components/configured-service/hooks/useMapVisibleMarkers'
import AdaptiveServiceMarkerLayer from 'components/configured-service/Schedule/AdaptiveServiceMarkerLayer'
import { getCanRenderIconLayer, useWebGLSupport } from 'components/common/map/webgl/useWebGLSupport'
import { SCHEDULING_MAP_SURFACE } from 'slices/configuredService/configuredServiceScheduleSlice'
import { useSharedMarkerPopover } from 'components/configured-service/Schedule/hooks/useSharedMarkerPopover'
import SchedulingMapServicePopover from 'components/configured-service/Schedule/SchedulingMapServicePopover'
import SkeletonSchedulingMapServicePopover from 'components/configured-service/Schedule/SkeletonSchedulingMapServicePopover'

import { StyledInfoWindow } from './MapStyles'

const { GOOGLE_MAPS_API_KEY } = Constants

const isValidCoord = value => typeof value === 'number' && !Number.isNaN(value)

const SchedulingMapLite = ({ latitude, longitude, onCenterChange, isAccountLoading }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const isLargeScreen = useMediaQuery(theme.breakpoints.up('lg'))
  const mapMarkerType = useSelector(selectCSDMapMarkerType, shallowEqual)
  const isMapReady = useSelector(selectCSDIsMapReady, shallowEqual)
  const mapCamera = useSelector(selectCSDMapCamera, shallowEqual)
  const currentTab = useSelector(selectCSDCurrentTab, shallowEqual)

  const webglSupported = useWebGLSupport()
  // The circle and pin are drawn inside the marker layer's deck.gl canvas, and WebGLMarkerSurface
  // only takes that path when the IconLayer texture probe passes too. Gate the DOM equivalents on
  // the same condition, or a device that clears basic WebGL2 but fails the probe gets markers with
  // no radius circle and no center pin.
  const webglMarkersActive = webglSupported && getCanRenderIconLayer()

  const [mapTypeId, setMapTypeId] = useState('roadmap')
  // Flips true on the map's first idle (tiles/markers painted) so the over-capacity notice waits for
  // the map to visually settle, not just for the data to load.
  const [hasSettled, setHasSettled] = useState(false)

  const skipNextCameraEventRef = useRef(false)
  const dataLengthRef = useRef(0)

  const { watch } = useFormContext()
  const radiusCommitted = watch('radiusCommitted')
  const watchedDays = watch(WEEK_DAYS.map(day => `selectedDays.${day}`))
  const selectedDaysMap = useMemo(() => {
    const days = {}
    WEEK_DAYS.forEach((day, index) => {
      days[day] = watchedDays?.[index] ?? true
    })
    return days
  }, [watchedDays])
  const startDate = watch('startDate')
  const endDate = watch('endDate')

  const formattedStartDate = useMemo(() => formatDateToBEFormatDateFns(startDate), [startDate])
  const formattedEndDate = useMemo(() => formatDateToBEFormatDateFns(endDate), [endDate])

  const parsedStartDate = useMemo(() => (startDate ? new Date(startDate) : null), [formattedStartDate])
  const parsedEndDate = useMemo(() => (endDate ? new Date(endDate) : null), [formattedEndDate])
  const effectiveEndDate = useMemo(
    () => parsedEndDate || (parsedStartDate ? endOfWeek(parsedStartDate) : null),
    [parsedEndDate, parsedStartDate]
  )
  const isScheduleTab = useMemo(() => currentTab === CREATE_DRAFT_CS_TABS.SCHEDULE, [currentTab])

  const {
    data,
    totalMatched,
    truncated,
    cap,
    isLoading: isMapDataLoading,
    isFetching,
  } = useConfiguredServiceMapData({
    latitude,
    longitude,
    surface: SCHEDULING_MAP_SURFACE.NEW_SERVICE_SCHEDULE,
    additionalDependencies: [currentTab],
  })
  const isLoading = isMapDataLoading || isAccountLoading

  const { setViewportBounds, deferredMarkers } = useMapVisibleMarkers(data, isLoading)

  const {
    activeMarker,
    infoWindowShown,
    dialogOpen,
    serviceDetails,
    weekWorkOrders,
    isLoadingPopover,
    handleMarkerMouseEnter,
    handleMarkerMouseLeave,
    handleMarkerClick,
    handlePopoverMouseEnter,
    handlePopoverMouseLeave,
    handleDialogClose,
  } = useSharedMarkerPopover({ startDate: parsedStartDate, endDate: effectiveEndDate })

  const showCenterElements = isValidCoord(latitude) && isValidCoord(longitude) && !isLoading
  const centerLatLng = useMemo(() => ({ lat: latitude, lng: longitude }), [latitude, longitude])

  // Handed to the marker layer so the WebGL path draws these in its own canvas — a separate
  // DOM circle/marker would stack unpredictably against the deck.gl overlay.
  const radiusCircle = useMemo(
    () =>
      showCenterElements
        ? {
            center: centerLatLng,
            radius: Math.max(0, Math.round(radiusCommitted || 0)),
            strokeColor: blue[800],
            strokeOpacity: 1,
            strokeWeight: 3,
            fillColor: blue[500],
            fillOpacity: 0.3,
          }
        : null,
    [showCenterElements, centerLatLng, radiusCommitted]
  )

  const centerMarker = useMemo(
    () => (showCenterElements ? { position: centerLatLng, bgColor: blue[500] } : null),
    [showCenterElements, centerLatLng]
  )

  const handleCameraChange = useCallback(
    e => {
      if (skipNextCameraEventRef.current) {
        skipNextCameraEventRef.current = false
        return
      }
      const newCameraProps = e.detail
      dispatch(setMapCamera(newCameraProps))

      if (newCameraProps.bounds) {
        setViewportBounds(newCameraProps.bounds)
      }

      if (onCenterChange && newCameraProps.center) {
        onCenterChange(newCameraProps.center)
      }
    },
    [onCenterChange, dispatch]
  )

  const handleAlignment = useCallback(
    (event, newMarkerType) => {
      if (newMarkerType !== null) {
        dispatch(setMapMarkerType(newMarkerType))
      }
    },
    [dispatch]
  )

  const handleMapTypeChange = useCallback((event, newMapTypeId) => {
    if (newMapTypeId !== null) {
      setMapTypeId(newMapTypeId)
    }
  }, [])

  const handleIdle = useCallback(() => {
    setHasSettled(true)
    dispatch(setIsMapReady(true))
  }, [dispatch])

  const initializeCamera = useCallback(() => {
    if (isValidCoord(latitude) && isValidCoord(longitude)) {
      skipNextCameraEventRef.current = true
      dispatch(
        setMapCamera({
          center: {
            lat: latitude,
            lng: longitude,
          },
          zoom: 18,
        })
      )
    }
  }, [latitude, longitude])

  useEffect(() => {
    initializeCamera()
  }, [])

  useEffect(() => {
    dataLengthRef.current = data?.length ?? 0
  }, [data])

  useEffect(() => {
    if (dataLengthRef.current >= MAX_AUTO_FIT_MARKER_COUNT) return
    initializeCamera()
  }, [latitude, longitude, isMapReady, isScheduleTab, initializeCamera])

  const pickerPosition = isLargeScreen ? ControlPosition.TOP_CENTER : ControlPosition.TOP_LEFT

  return (
    <Box flex={1} width="100%" height="100%" sx={{ position: 'relative' }}>
      {(isLoading || isFetching) && (
        <LinearProgress
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            zIndex: 1000,
            height: 4,
          }}
        />
      )}
      <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
        <Map
          disableDefaultUI
          zoomControl
          zoomControlOptions={{
            position: ControlPosition.TOP_LEFT,
          }}
          mapId="cs-scheduling"
          maxZoom={GOOGLE_MAPS_MAX_ZOOM}
          onCameraChanged={handleCameraChange}
          onIdle={handleIdle}
          mapTypeId={mapTypeId}
          {...mapCamera}
        >
          <MapControl position={pickerPosition}>
            <Box mt={1} ml={!isLargeScreen ? 1 : 0}>
              <WeekPicker />
            </Box>
          </MapControl>

          <MapTypeControlWrapper value={mapTypeId} onChange={handleMapTypeChange} />

          <MapControl position={ControlPosition.TOP_RIGHT}>
            <MarkerTypeControl value={mapMarkerType} onChange={handleAlignment} sx={{ mt: 1, mr: 1 }} />
          </MapControl>

          {/* Gate the cap count on the map having settled, so it isn't asserted mid-initial-load. */}
          <MapControl position={ControlPosition.BOTTOM_LEFT}>
            <MapRadiusControl truncated={hasSettled && !isLoading && truncated} totalMatched={totalMatched} cap={cap} />
          </MapControl>
          <AdaptiveServiceMarkerLayer
            markers={deferredMarkers}
            mapMarkerType={mapMarkerType}
            activeMarker={activeMarker}
            selectedDaysMap={selectedDaysMap}
            onMouseEnter={handleMarkerMouseEnter}
            onMouseLeave={handleMarkerMouseLeave}
            onClick={handleMarkerClick}
            radiusCircle={radiusCircle}
            centerMarker={centerMarker}
          />

          {showCenterElements && !webglMarkersActive && (
            <>
              <Circle
                zIndex={1}
                center={{
                  lat: latitude,
                  lng: longitude,
                }}
                strokeColor={blue[800]}
                strokeOpacity={1}
                strokeWeight={3}
                fillColor={blue[500]}
                fillOpacity={0.3}
                radius={Math.max(0, Math.round(radiusCommitted || 0))}
              />
              <AdvancedMarker zIndex={1} position={{ lat: latitude, lng: longitude }}>
                <Box
                  backgroundColor={blue[500]}
                  width={40}
                  height={40}
                  borderRadius="50%"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  sx={{ cursor: 'pointer' }}
                >
                  <SvgIcon
                    sx={{
                      color: theme.palette.common.white,
                    }}
                    width={24}
                    height={24}
                  >
                    <CSMarker />
                  </SvgIcon>
                </Box>
              </AdvancedMarker>
            </>
          )}

          {infoWindowShown && activeMarker && (
            <StyledInfoWindow
              disableAutoPan
              // @vis.gl/react-google-maps treats `anchor={null}` as "anchor not ready yet" and
              // bails out — the WebGL path has no DOM marker, so position it by latLng instead.
              anchor={activeMarker.anchor || undefined}
              position={!activeMarker.anchor ? activeMarker.latLng : undefined}
              headerDisabled
            >
              <Box onMouseEnter={handlePopoverMouseEnter} onMouseLeave={handlePopoverMouseLeave}>
                {isLoadingPopover ? (
                  <SkeletonSchedulingMapServicePopover />
                ) : (
                  <SchedulingMapServicePopover
                    service={
                      serviceDetails
                        ? {
                            ...activeMarker.details,
                            servicePrice: formatCentsToDollars(get(serviceDetails, 'servicePriceCents')),
                            pricingPeriodInterval: get(serviceDetails, 'pricingPeriod.periodInterval'),
                            pricingPeriodPer: get(serviceDetails, 'pricingPeriod.periodPer'),
                            quantity: get(serviceDetails, 'quantity'),
                            measureUnit: get(serviceDetails, 'measure.unit'),
                            measureVolume: get(serviceDetails, 'measure.volume'),
                            methodType: get(serviceDetails, 'methodType') ?? get(serviceDetails, 'method'),
                          }
                        : activeMarker.details
                    }
                    weekWorkOrders={weekWorkOrders}
                  />
                )}
              </Box>
            </StyledInfoWindow>
          )}
        </Map>
      </APIProvider>

      {dialogOpen && activeMarker && (
        <Dialog open={dialogOpen} onClose={handleDialogClose} fullWidth maxWidth="xs" sx={{ zIndex: t => Z_INDEX.MENU(t) + 10 }}>
          <HHDialogTitle onClose={handleDialogClose} />
          <DialogContent>
            {isLoadingPopover ? (
              <SkeletonSchedulingMapServicePopover sx={{ width: '100%' }} />
            ) : (
              <SchedulingMapServicePopover
                sx={{ width: '100%' }}
                service={
                  serviceDetails
                    ? {
                        ...activeMarker.details,
                        servicePrice: formatCentsToDollars(get(serviceDetails, 'servicePriceCents')),
                        pricingPeriodInterval: get(serviceDetails, 'pricingPeriod.periodInterval'),
                        pricingPeriodPer: get(serviceDetails, 'pricingPeriod.periodPer'),
                        quantity: get(serviceDetails, 'quantity'),
                        measureUnit: get(serviceDetails, 'measure.unit'),
                        measureVolume: get(serviceDetails, 'measure.volume'),
                        methodType: get(serviceDetails, 'methodType') ?? get(serviceDetails, 'method'),
                      }
                    : activeMarker.details
                }
                weekWorkOrders={weekWorkOrders}
              />
            )}
          </DialogContent>
        </Dialog>
      )}
    </Box>
  )
}

SchedulingMapLite.propTypes = {
  latitude: PropTypes.number,
  longitude: PropTypes.number,
  onCenterChange: PropTypes.func,
  isAccountLoading: PropTypes.bool,
}

export default SchedulingMapLite
